import React, { useEffect, useMemo } from 'react'

import { useEditor } from '@toasttab/sites-components'

import { BorderAndShadowConfig, ImageFit } from 'src/apollo/sites'

import { getImageUrl } from 'shared/components/common/Image'

import { DO_PREFIX_REGEX } from 'public/components/default_template/hero/VideoHero'

import {
  getBorderRadiusStyle,
  getBorderStyle,
  getBoxShadowStyle
} from 'src/shared/utils/stylingUtils'
import { shouldApplyFirefoxEditorWorkaround } from '@toasttab/sites-components'

import { resources } from 'config'
import { useRestaurant } from '../restaurant_context/RestaurantContext'

export type EditableVideoProps = {
  editPath: string
  hideControls?: boolean
  autoplay?: boolean
  muted?: boolean
  aspectRatio?: string
  src: string | null
  thumbnailSrc?: string | null
  defaultThumbnailSrc?: string
  fit?: ImageFit
  borderAndShadowConfig?: BorderAndShadowConfig | null
  overrideGlobalBorderAndShadowConfig?: boolean | null
}

export const EditableVideo = (props: EditableVideoProps) => {
  const { restaurant } = useRestaurant()
  const designConfig = restaurant?.config?.designConfig?.borderAndShadowConfig

  const compositeConfig = useMemo(() => {
    if (props.overrideGlobalBorderAndShadowConfig) {
      return props.borderAndShadowConfig
    }

    return designConfig
  }, [
    designConfig,
    props.borderAndShadowConfig,
    props.overrideGlobalBorderAndShadowConfig
  ])

  const { useEditableRef, isEditor } = useEditor()
  const applyWorkaround = shouldApplyFirefoxEditorWorkaround(isEditor)

  const videoDisplaySrc = React.useMemo(
    () => props.src?.replace(DO_PREFIX_REGEX, resources.publicAssetURL),
    [props.src]
  )

  const { editableRef } = useEditableRef<HTMLVideoElement>({
    name: 'video',
    displayName: 'Video',
    actions: ['delete'],
    path: props.editPath,
    schema: { fields: [] }
  })

  const getThumbnailSrc = () => {
    if (props.thumbnailSrc) {
      return getImageUrl(props.thumbnailSrc)
    }
    return props.src ? undefined : getImageUrl(props.defaultThumbnailSrc)
  }

  useEffect(() => {
    // React won't consistently set muted attribute on video elements.
    // It mutes the video successfully, but doesn't apply the HTML attribute.
    // IOS reads the HTML attribute to determine if autoplay is allowed.
    // So here we force the HTML attribute to be set when muted is true, so that autoplay will work.
    if (editableRef.current && props.muted === true) {
      editableRef.current.muted = true
      editableRef.current.defaultMuted = true
    }
  }, [props.muted, editableRef])

  const videoClassName = props.fit === ImageFit.Contain ? 'contain' : 'cover'
  const emptyClassName = !videoDisplaySrc ? 'empty' : ''

  const borderAndShadowStyle = useMemo(() => {
    return {
      ...getBorderStyle(compositeConfig),
      ...getBoxShadowStyle(compositeConfig),
      ...getBorderRadiusStyle(compositeConfig)
    }
  }, [compositeConfig])

  return (
    <div
      className='videoContainer'
      style={{
        aspectRatio: props.aspectRatio,
        ...(applyWorkaround && { position: 'relative' })
      }}
    >
      {applyWorkaround && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            cursor: 'pointer',
            pointerEvents: 'auto',
            zIndex: 1
          }}
          aria-hidden='true'
          data-testid='firefox-video-overlay'
        />
      )}
      <video
        ref={editableRef}
        data-testid='editable-video'
        poster={getThumbnailSrc()}
        controls={!props.hideControls}
        autoPlay={props.autoplay}
        loop={props.autoplay}
        muted={props.muted}
        playsInline
        preload={getThumbnailSrc() ? 'metadata' : 'auto'}
        src={getImageUrl(videoDisplaySrc)}
        style={{
          ...borderAndShadowStyle,
          ...(applyWorkaround && { pointerEvents: 'none' })
        }}
        className={`video ${videoClassName} ${emptyClassName}`}
      />
    </div>
  )
}
